import React, { useState, useEffect, useRef } from 'react';
import { colors, baseStyles, formatUSD, formatNum, DeltaBadge, MetricCard, useWindowSize, formatSmartPrice } from './SharedUI';

const BattlePanel = ({ type, data, baseline, isMobile, liveDataRef }) => {
  const isLong = type === 'LONG';
  const color = isLong ? colors.green : colors.red;
  const bgHeader = isLong ? colors.greenBg : colors.redBg;

  const fallbackAvg = isLong ? data.longTradersAvgEntryPrice : data.shortTradersAvgEntryPrice;
  const currentPrice = liveDataRef?.current?.last || fallbackAvg;

  const baseTotalQty = isLong ? baseline.longTradersQty : baseline.shortTradersQty;
  const baseTotalTraders = isLong ? baseline.longTraders : baseline.shortTraders;
  const baseWhaleQty = isLong ? baseline.longWhalesQty : baseline.shortWhalesQty;
  const baseWhaleTraders = isLong ? baseline.longWhales : baseline.shortWhales;
  const baseRetailQty = baseTotalQty - baseWhaleQty;
  const baseRetailTraders = baseTotalTraders - baseWhaleTraders;

  const totalQty = isLong ? data.longTradersQty : data.shortTradersQty;
  const totalTraders = isLong ? data.longTraders : data.shortTraders;
  const totalAvg = isLong ? data.longTradersAvgEntryPrice : data.shortTradersAvgEntryPrice;
  const totalOI = totalQty * currentPrice;

  const whaleQty = isLong ? data.longWhalesQty : data.shortWhalesQty;
  const whaleTraders = isLong ? data.longWhales : data.shortWhales;
  const whaleAvg = isLong ? data.longWhalesAvgEntryPrice : data.shortWhalesAvgEntryPrice;
  const whaleOI = whaleQty * currentPrice;

  const retailQty = totalQty - whaleQty;
  const retailTraders = totalTraders - whaleTraders;
  const retailOI = retailQty * currentPrice;
  const retailAvg = retailQty > 0 ? ((totalQty * totalAvg) - (whaleQty * whaleAvg)) / retailQty : 0;

  const whaleVolPct = totalQty > 0 ? ((whaleQty / totalQty) * 100).toFixed(1) : 0;
  const retailVolPct = totalQty > 0 ? ((retailQty / totalQty) * 100).toFixed(1) : 0;
  const whaleTraderPct = totalTraders > 0 ? ((whaleTraders / totalTraders) * 100).toFixed(1) : 0;
  const retailTraderPct = totalTraders > 0 ? ((retailTraders / totalTraders) * 100).toFixed(1) : 0;

  const getAvgColor = (avg) => {
    if (!currentPrice || avg === 0) return colors.textMain;
    if (isLong) return avg < currentPrice ? colors.green : colors.red;
    return avg > currentPrice ? colors.green : colors.red;            
  };

  const getPnLPct = (avg) => {
    if (!currentPrice || avg === 0) return '';
    let diff = 0;
    if (isLong) {
      diff = ((currentPrice - avg) / avg) * 100; 
    } else {
      diff = ((avg - currentPrice) / avg) * 100; 
    }
    return `(${diff > 0 ? '+' : ''}${diff.toFixed(2)}%)`;
  };

  const tableDynamicStyle = { minWidth: isMobile ? '100%' : '450px', fontSize: isMobile ? '11px' : '12px' };
  const rowDynamicStyle = { minWidth: isMobile ? '100%' : '450px', fontSize: isMobile ? '11px' : '13px' };

  return (
    <div style={{ ...baseStyles.card, border: `1px solid ${color}`, padding: isMobile ? '12px' : '16px' }}>
      <div style={{ backgroundColor: bgHeader, padding: '12px', margin: isMobile ? '-12px -12px 12px -12px' : '-16px -16px 16px -16px', borderTopLeftRadius: '11px', borderTopRightRadius: '11px', color: color, fontWeight: 'bold', fontSize: '14px' }}>
        {isLong ? '🟢 PHE LONG (BÒ) - BULLISH' : '🔴 PHE SHORT (GẤU) - BEARISH'}
      </div>
      <div style={baseStyles.sectionTitle}>Thống kê tổng quan</div>
      <div style={baseStyles.tableContainer}>
        <div style={{...baseStyles.tableGrid, ...tableDynamicStyle, color: colors.textSub}}>
          <div>Phân loại</div><div>Vị thế</div><div>Qty (BTC)</div><div>Trader</div><div>Avg Price</div>
        </div>
        
        <div style={{...baseStyles.row, ...rowDynamicStyle, fontWeight: 'bold'}}>
          <div>TOTAL</div>
          <div>{formatUSD(totalOI)}</div>
          <div style={{display:'flex', alignItems:'center', flexWrap: 'wrap'}}>{formatSmartPrice(totalQty)} <DeltaBadge current={totalQty} base={baseTotalQty}/></div>
          <div style={{display:'flex', alignItems:'center', flexWrap: 'wrap'}}>{totalTraders} <DeltaBadge current={totalTraders} base={baseTotalTraders}/></div>
          <div style={{color: getAvgColor(totalAvg)}}>
            ${formatSmartPrice(totalAvg)} <span style={{fontSize: '11px', fontWeight: 'normal', marginLeft: '4px'}}>{getPnLPct(totalAvg)}</span>
          </div>
        </div>

        <div style={{...baseStyles.row, ...rowDynamicStyle}}>
          <div>WHALE</div>
          <div style={{color}}>{formatUSD(whaleOI)}</div>
          <div style={{color, display:'flex', alignItems:'center', flexWrap: 'wrap'}}>{formatSmartPrice(whaleQty)} <DeltaBadge current={whaleQty} base={baseWhaleQty}/></div>
          <div style={{color, display:'flex', alignItems:'center', flexWrap: 'wrap'}}>{whaleTraders} <DeltaBadge current={whaleTraders} base={baseWhaleTraders}/></div>
          <div style={{color: getAvgColor(whaleAvg)}}>
            ${formatSmartPrice(whaleAvg)} <span style={{fontSize: '11px', marginLeft: '4px'}}>{getPnLPct(whaleAvg)}</span>
          </div>
        </div>

        <div style={{...baseStyles.row, ...rowDynamicStyle}}>
          <div>RETAIL</div>
          <div style={{color: colors.textSub}}>{formatUSD(retailOI)}</div>
          <div style={{color: colors.textSub, display:'flex', alignItems:'center', flexWrap: 'wrap'}}>{formatSmartPrice(retailQty)} <DeltaBadge current={retailQty} base={baseRetailQty}/></div>
          <div style={{color: colors.textSub, display:'flex', alignItems:'center', flexWrap: 'wrap'}}>{retailTraders} <DeltaBadge current={retailTraders} base={baseRetailTraders}/></div>
          <div style={{color: getAvgColor(retailAvg)}}>
            ${formatSmartPrice(retailAvg)} <span style={{fontSize: '11px', marginLeft: '4px'}}>{getPnLPct(retailAvg)}</span>
          </div>
        </div>
      </div>

      <div style={{ backgroundColor: 'rgba(0,0,0,0.2)', padding: '12px', borderRadius: '8px', marginTop: '16px' }}>
        <div style={baseStyles.flexBetween}><span style={{ minWidth: isMobile ? '100%' : '230px', color: colors.textSub, marginBottom: isMobile ? '4px' : '0' }}>Tỷ trọng Volume Cá mập (Coin/Tiền):</span><strong style={{ color: whaleVolPct > 50 ? colors.green : colors.red, textAlign: isMobile ? 'left' : 'right' }}>{whaleVolPct}%</strong></div>
        <div style={{ ...baseStyles.flexBetween, marginTop: '12px' }}><span style={{ minWidth: isMobile ? '100%' : '230px', color: colors.textSub, marginBottom: isMobile ? '4px' : '0' }}>Tỷ trọng Trader Cá mập (Tài khoản):</span><strong style={{ color: whaleTraderPct > 50 ? colors.green : colors.red, textAlign: isMobile ? 'left' : 'right' }}>{whaleTraderPct}%</strong></div>
      </div>
    </div>
  );
};

function useSmartMoneyData(symbol, pollingInterval = 30000) {
  const [data, setData] = useState(null);
  const [baseline, setBaseline] = useState(null);
  const [loading, setLoading] = useState(true);
  const isFirstLoad = useRef(true);

  useEffect(() => {
    let intervalId;
    isFirstLoad.current = true; setLoading(true); setBaseline(null); setData(null);

    const fetchData = async () => {
      try {
        const response = await fetch(`https://www.binance.com/bapi/futures/v1/public/future/smart-money/signal/overview?symbol=${symbol.toLowerCase()}`);
        const json = await response.json();
        if (json.success && json.data) {
          setData(json.data);
          if (isFirstLoad.current) { setBaseline(json.data); isFirstLoad.current = false; }
        }
      } catch (error) {
        console.error(error);
      } finally { setLoading(false); }
    };

    fetchData();
    intervalId = setInterval(fetchData, pollingInterval);
    return () => clearInterval(intervalId);
  }, [symbol, pollingInterval]);

  return { data, baseline, loading };
}

export default function SmartMoneySection({ symbol, smartMoneyRef, liveDataRef }) {
  const { width } = useWindowSize();
  const isMobile = width < 768; 
  const isTablet = width >= 768 && width < 1024; 
  const { data, baseline, loading } = useSmartMoneyData(symbol, 15000);

  useEffect(() => {
    if (data && smartMoneyRef) {
      smartMoneyRef.current = data;
    }
  }, [data, smartMoneyRef]);

  if (loading || !data || !baseline) return <div style={{...baseStyles.card, textAlign: 'center'}}>Đang tải dữ liệu Cá Mập...</div>;

  const lwTotal = data.longTraders > 0 ? (data.longProfitTraders / data.longTraders) * 100 : 0;
  const lwWhale = data.longWhales > 0 ? (data.longProfitWhales / data.longWhales) * 100 : 0;
  const lwRetail = (data.longTraders - data.longWhales) > 0 ? ((data.longProfitTraders - data.longProfitWhales) / (data.longTraders - data.longWhales)) * 100 : 0;

  const swTotal = data.shortTraders > 0 ? (data.shortProfitTraders / data.shortTraders) * 100 : 0;
  const swWhale = data.shortWhales > 0 ? (data.shortProfitWhales / data.shortWhales) * 100 : 0;
  const swRetail = (data.shortTraders - data.shortWhales) > 0 ? ((data.shortProfitTraders - data.shortProfitWhales) / (data.shortTraders - data.shortWhales)) * 100 : 0;

  const currentPrice = liveDataRef?.current?.last;
  
  const calcPnL = (isLong, qty, avgPrice) => {
    if (!currentPrice || !avgPrice || !qty) return 0;
    return isLong ? qty * (currentPrice - avgPrice) : qty * (avgPrice - currentPrice);
  };

  const longTotalPnL = calcPnL(true, data.longTradersQty, data.longTradersAvgEntryPrice);
  const longWhalePnL = calcPnL(true, data.longWhalesQty, data.longWhalesAvgEntryPrice);
  const longRetailPnL = longTotalPnL - longWhalePnL; 

  const shortTotalPnL = calcPnL(false, data.shortTradersQty, data.shortTradersAvgEntryPrice);
  const shortWhalePnL = calcPnL(false, data.shortWhalesQty, data.shortWhalesAvgEntryPrice);
  const shortRetailPnL = shortTotalPnL - shortWhalePnL;

  return (
    <div style={{ marginTop: '32px', borderTop: `2px dashed ${colors.border}`, paddingTop: '32px' }}>
      <h2 style={{ fontSize: '18px', marginBottom: '20px', color: colors.textMain }}>🐳 SMART MONEY RADAR (CÁ MẬP VS NHỎ LẺ)</h2>
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : (isTablet ? 'repeat(4, 1fr)' : 'repeat(4, 1fr)'), gap: '12px', marginBottom: '20px' }}>
        <MetricCard label="TỔNG VỊ THẾ (O.I)" currentVal={data.totalPositions} baseVal={baseline.totalPositions} format="usd" />
        <MetricCard label="TỔNG TRADER" currentVal={data.totalTraders} baseVal={baseline.totalTraders} />
        <MetricCard label="POS VOL RATIO (L/S)" currentVal={data.longShortRatio} baseVal={baseline.longShortRatio} valueColor={data.longShortRatio > 1 ? colors.green : colors.red} />
        <MetricCard label="ACCOUNT RATIO (L/S)" currentVal={data.longTraders/data.shortTraders} baseVal={baseline.longTraders/baseline.shortTraders} valueColor={(data.longTraders/data.shortTraders) > 1 ? colors.green : colors.red} />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)', gap: '20px', marginBottom: '20px' }}>
         <BattlePanel type="LONG" data={data} baseline={baseline} isMobile={isMobile} liveDataRef={liveDataRef} />
         <BattlePanel type="SHORT" data={data} baseline={baseline} isMobile={isMobile} liveDataRef={liveDataRef} />
      </div>

      <div style={baseStyles.card}>
        <h2 style={{ textAlign: 'center', marginTop: 0, marginBottom: '20px', fontSize: '16px' }}>HIỆU SUẤT & LỢI NHUẬN</h2>
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)', gap: '20px' }}>
          
          <div style={{ borderRight: isMobile ? 'none' : `1px solid ${colors.border}`, borderBottom: isMobile ? `1px solid ${colors.border}` : 'none', paddingRight: isMobile ? 0 : '20px', paddingBottom: isMobile ? '20px' : 0 }}>
            <div style={{ color: colors.green, fontWeight: 'bold', marginBottom: '12px', fontSize: '14px' }}>🟢 WIN RATE PHE LONG</div>
            <div style={baseStyles.flexBetween}><span style={{color: colors.textSub, minWidth: '100px'}}>Tổng Win Rate:</span><span style={{textAlign: 'right'}}>{lwTotal.toFixed(1)}% ({data.longProfitTraders}/{data.longTraders})</span></div>
            <div style={baseStyles.flexBetween}><span style={{color: colors.textSub, minWidth: '100px'}}>Cá mập (Whale):</span><strong style={{color: lwWhale > lwRetail ? colors.green : colors.red, textAlign: 'right'}}>{lwWhale.toFixed(1)}%</strong></div>
            <div style={baseStyles.flexBetween}><span style={{color: colors.textSub, minWidth: '100px'}}>Nhỏ lẻ (Retail):</span><strong style={{color: lwRetail > lwWhale ? colors.green : colors.red, textAlign: 'right'}}>{lwRetail.toFixed(1)}%</strong></div>
            
            <div style={{ borderTop: `1px dashed ${colors.border}`, marginTop: '12px', paddingTop: '12px' }}>
              <div style={{ color: colors.textMain, fontWeight: 'bold', marginBottom: '8px', fontSize: '12px' }}>💰 TRẠNG THÁI PnL (ƯỚC TÍNH)</div>
              <div style={baseStyles.flexBetween}>
                <span style={{color: colors.textSub, minWidth: '100px'}}>Toàn phe Long:</span>
                <strong style={{color: longTotalPnL >= 0 ? colors.green : colors.red, textAlign: 'right'}}>{longTotalPnL > 0 ? '+' : ''}{formatUSD(longTotalPnL)}</strong>
              </div>
              <div style={baseStyles.flexBetween}>
                <span style={{color: colors.textSub, minWidth: '100px'}}>Cá mập (Whale):</span>
                <strong style={{color: longWhalePnL >= 0 ? colors.green : colors.red, textAlign: 'right'}}>{longWhalePnL > 0 ? '+' : ''}{formatUSD(longWhalePnL)}</strong>
              </div>
              <div style={baseStyles.flexBetween}>
                <span style={{color: colors.textSub, minWidth: '100px'}}>Nhỏ lẻ (Retail):</span>
                <strong style={{color: longRetailPnL >= 0 ? colors.green : colors.red, textAlign: 'right'}}>{longRetailPnL > 0 ? '+' : ''}{formatUSD(longRetailPnL)}</strong>
              </div>
            </div>
          </div>

          <div style={{ paddingLeft: isMobile ? 0 : '8px' }}>
             <div style={{ color: colors.red, fontWeight: 'bold', marginBottom: '12px', fontSize: '14px' }}>🔴 WIN RATE PHE SHORT</div>
            <div style={baseStyles.flexBetween}><span style={{color: colors.textSub, minWidth: '100px'}}>Tổng Win Rate:</span><span style={{textAlign: 'right'}}>{swTotal.toFixed(1)}% ({data.shortProfitTraders}/{data.shortTraders})</span></div>
            <div style={baseStyles.flexBetween}><span style={{color: colors.textSub, minWidth: '100px'}}>Cá mập (Whale):</span><strong style={{color: swWhale > swRetail ? colors.green : colors.red, textAlign: 'right'}}>{swWhale.toFixed(1)}%</strong></div>
            <div style={baseStyles.flexBetween}><span style={{color: colors.textSub, minWidth: '100px'}}>Nhỏ lẻ (Retail):</span><strong style={{color: swRetail > swWhale ? colors.green : colors.red, textAlign: 'right'}}>{swRetail.toFixed(1)}%</strong></div>
            
            <div style={{ borderTop: `1px dashed ${colors.border}`, marginTop: '12px', paddingTop: '12px' }}>
              <div style={{ color: colors.textMain, fontWeight: 'bold', marginBottom: '8px', fontSize: '12px' }}>💰 TRẠNG THÁI PnL (ƯỚC TÍNH)</div>
              <div style={baseStyles.flexBetween}>
                <span style={{color: colors.textSub, minWidth: '100px'}}>Toàn phe Short:</span>
                <strong style={{color: shortTotalPnL >= 0 ? colors.green : colors.red, textAlign: 'right'}}>{shortTotalPnL > 0 ? '+' : ''}{formatUSD(shortTotalPnL)}</strong>
              </div>
              <div style={baseStyles.flexBetween}>
                <span style={{color: colors.textSub, minWidth: '100px'}}>Cá mập (Whale):</span>
                <strong style={{color: shortWhalePnL >= 0 ? colors.green : colors.red, textAlign: 'right'}}>{shortWhalePnL > 0 ? '+' : ''}{formatUSD(shortWhalePnL)}</strong>
              </div>
              <div style={baseStyles.flexBetween}>
                <span style={{color: colors.textSub, minWidth: '100px'}}>Nhỏ lẻ (Retail):</span>
                <strong style={{color: shortRetailPnL >= 0 ? colors.green : colors.red, textAlign: 'right'}}>{shortRetailPnL > 0 ? '+' : ''}{formatUSD(shortRetailPnL)}</strong>
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}
